import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent } from '@theme/dialog';
import { isMobileBreakpoint, fetchConfig } from '@theme/utilities';

/** How long (ms) the error message is shown on the submit button. */
const ERROR_DISPLAY_DURATION = 5000;

/**
 * CartEditTrigger – wraps each cart "Edit" link.
 * On click it fetches the product page HTML, extracts the product grid,
 * patches it for cart-edit behaviour, morphs it into the cart-edit-dialog,
 * and opens the modal.
 */
export class CartEditTrigger extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  get productUrl() {
    return this.dataset.productUrl || '';
  }

  get itemQuantity() {
    return this.dataset.itemQuantity || '1';
  }

  get itemKey() {
    return this.dataset.itemKey || '';
  }

  get variantId() {
    return this.dataset.variantId || '';
  }

  get productTitle() {
    return this.dataset.productTitle || '';
  }

  /**
   * Handles the click on the edit link
   * @param {Event} event
   */
  handleEditClick = async (event) => {
    event.preventDefault();

    const productUrl = this.productUrl;
    if (!productUrl) return;

    const html = await this.#fetchProductPage(productUrl);
    if (!html) return;

    const productGrid = html.querySelector('[data-product-grid-content]');
    if (!productGrid) return;

    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));

    // Prepare the content for cart-edit behaviour BEFORE morphing into DOM
    patchProductGridForCartEdit(freshContent);

    await this.#updateCartEditModal(freshContent);

    this.#openCartEditModal();
  };

  /**
   * Fetches the product page content
   * @param {string} url
   * @returns {Promise<Document | null>}
   */
  async #fetchProductPage(url) {
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    try {
      const response = await fetch(url, {
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    } finally {
      // Only clear the reference if it still points to *this* call's controller.
      // A newer call may have already replaced it — don't wipe that out.
      if (this.#abortController === controller) {
        this.#abortController = null;
      }
    }
  }

  /**
   * Updates the cart edit modal content
   * @param {Element} productGrid
   */
  async #updateCartEditModal(productGrid) {
    const modalContent = document.getElementById('cart-edit-modal-content');
    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.productTitle;
      productTitle.href = this.productUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);

      if (productPrice) {
        productHeader.appendChild(productPrice);
      }

      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }

      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    // After morph, sync the checked state of radio inputs.
    // morph sets HTML attributes but doesn't reliably set the DOM `checked`
    // property, so :has(:checked) CSS selectors won't match.
    syncRadioCheckedState(modalContent);
  }

  /**
   * Opens the cart edit modal dialog
   */
  #openCartEditModal() {
    const dialogComponent = document.getElementById('cart-edit-dialog');
    if (!(dialogComponent instanceof CartEditDialog)) return;

    dialogComponent.setCartLineData({
      itemQuantity: this.itemQuantity,
      itemKey: this.itemKey,
      variantId: this.variantId,
    });
    dialogComponent.showDialog();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }
}

if (!customElements.get('cart-edit-trigger')) {
  customElements.define('cart-edit-trigger', CartEditTrigger);
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Strips color-swatch-navigation from all inputs inside the given element
 * and converts them to use data-connected-product-url instead.
 * @param {Element} container
 */
function stripColorSwatchNavigation(container) {
  const inputs = container.querySelectorAll('input[data-color-swatch-navigation]');
  for (const input of inputs) {
    const targetUrl = input.getAttribute('data-color-target-url') || '';
    input.removeAttribute('data-color-swatch-navigation');
    input.removeAttribute('data-color-target-url');
    if (targetUrl) {
      input.setAttribute('data-connected-product-url', targetUrl);
    }
  }
}

/**
 * After morph, ensures radio inputs with `data-current-checked="true"` or
 * the HTML `checked` attribute actually have their DOM `checked` property
 * set to true. morph libraries often copy HTML attributes but don't set the
 * IDL property, so :has(:checked) CSS won't match.
 * @param {Element} container
 */
function syncRadioCheckedState(container) {
  // Combined listing color swatches use data-current-checked="true"
  const currentChecked = container.querySelectorAll('input[type="radio"][data-current-checked="true"]');
  for (const input of currentChecked) {
    if (input instanceof HTMLInputElement && !input.checked) {
      input.checked = true;
    }
  }

  // Normal variant options use the checked HTML attribute
  const htmlChecked = container.querySelectorAll('input[type="radio"][checked]');
  for (const input of htmlChecked) {
    if (input instanceof HTMLInputElement && !input.checked) {
      input.checked = true;
    }
  }
}

/**
 * Patches a product grid element for cart-edit modal use:
 * 1. Removes `on:submit` from product-form-component.
 * 2. Strips color-swatch-navigation attributes.
 * 3. Removes data-template-product-match from variant-picker.
 * 4. Changes "Add to cart" button text to "Update".
 * @param {Element} productGrid
 */
function patchProductGridForCartEdit(productGrid) {
  // 1. Remove on:submit so the framework's document-level delegation
  //    won't call product-form-component's handleSubmit (cart add).
  const productFormComponent = productGrid.querySelector('product-form-component');
  if (productFormComponent) {
    productFormComponent.removeAttribute('on:submit');
  }

  // 2. Fix combined listing color swatches
  stripColorSwatchNavigation(productGrid);

  // 3. Prevent variant picker from acting as if on the PDP
  const variantPicker = productGrid.querySelector('variant-picker');
  if (variantPicker) {
    variantPicker.removeAttribute('data-template-product-match');
  }

  // 4. Change button text to "Update"
  replaceAddToCartText(productGrid);
}

/**
 * Finds the add-to-cart button inside an element and changes its text to "Update".
 * @param {Element} container
 */
function replaceAddToCartText(container) {
  const button = container.querySelector('button[name="add"], button[type="submit"]');
  if (!button) return;

  const defaultSpans = button.querySelectorAll('.add-to-cart-text--default');
  if (defaultSpans.length > 0) {
    for (const span of defaultSpans) {
      span.textContent = 'Update';
    }
    return;
  }

  // Fallback: look for spans with common add-to-cart text
  const allSpans = button.querySelectorAll('span');
  for (const span of allSpans) {
    const text = span.textContent?.trim().toLowerCase() || '';
    if (text.includes('add to cart') || text.includes('add to bag')) {
      span.textContent = 'Update';
    }
  }
}

/**
 * Collects section IDs from cart-items-component elements for section rendering.
 * @returns {string}
 */
function getSectionsParam() {
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionIds = /** @type {string[]} */ ([]);
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });
  return sectionIds.join(',');
}

/**
 * Shows a temporary error message on a submit button and re-enables it.
 *
 * @param {HTMLButtonElement | null} button
 * @param {string} message
 */
function showButtonError(button, message) {
  if (!button) return;

  button.disabled = false;

  // Find or create an error span inside the button
  let errorSpan = button.querySelector('.cart-edit-error');
  if (!errorSpan) {
    errorSpan = document.createElement('span');
    errorSpan.classList.add('cart-edit-error');
    errorSpan.setAttribute('role', 'alert');
    button.appendChild(errorSpan);
  }
  errorSpan.textContent = message;

  // Hide the normal button text
  const normalText = button.querySelector('.add-to-cart-text');
  if (normalText instanceof HTMLElement) {
    normalText.style.display = 'none';
  }

  // Restore after a delay
  setTimeout(() => {
    errorSpan?.remove();
    if (normalText instanceof HTMLElement) {
      normalText.style.display = '';
    }
  }, ERROR_DISPLAY_DURATION);
}

// ---------------------------------------------------------------------------
// CartEditDialog
// ---------------------------------------------------------------------------

/**
 * CartEditDialog – the modal dialog for editing cart items.
 * Intercepts form submission to do a cart change instead of cart add.
 */
class CartEditDialog extends DialogComponent {
  #abortController = new AbortController();

  /**
   * Reference to the parent DialogComponent's closeDialog method.
   * DialogComponent defines closeDialog as an arrow-function class field
   * (instance property), so `super.closeDialog` won't work — super only
   * resolves prototype methods. We capture the parent's version here
   * (class fields are assigned in declaration order, and the parent's
   * fields are already set after super() runs) before our own override
   * replaces it below.
   * @type {() => Promise<void>}
   */
  #parentCloseDialog = this.closeDialog;

  /** @type {string} */
  #itemQuantity = '1';
  /** @type {string} */
  #itemKey = '';
  /** @type {string} */
  #originalVariantId = '';
  /** @type {MutationObserver | null} */
  #contentObserver = null;
  /**
   * When true, #handleCartUpdate will NOT auto-close the dialog.
   * Set during error-recovery paths where #refreshCartUI is called
   * but we still need the dialog open to show an error message.
   * @type {boolean}
   */
  #suppressAutoClose = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Listen for cartUpdate to auto-close on success
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });

    // Capture form submit – backup; primary mechanism is removing on:submit.
    this.addEventListener('submit', this.#handleFormSubmit, { capture: true, signal });

    // After variant picker updates (combined listing product change),
    // re-patch the DOM immediately.
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // CRITICAL: Intercept click events BEFORE the browser fires the `change`
    // event on color-swatch radio inputs. The variant picker's own `change`
    // handler checks data-color-swatch-navigation and navigates. By stripping
    // the attribute during the `click` phase (which precedes `change`), we
    // prevent the navigation entirely.
    this.addEventListener('click', this.#handleClick, { capture: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#stopObserving();
  }

  /**
   * Sets the cart line data for the item being edited
   * @param {{ itemQuantity: string, itemKey: string, variantId: string }} data
   */
  setCartLineData(data) {
    this.#itemQuantity = data.itemQuantity;
    this.#itemKey = data.itemKey;
    this.#originalVariantId = data.variantId;
  }

  /** @override */
  showDialog() {
    super.showDialog();
    this.#observeModalContent();
  }

  /** @override – stops the MutationObserver regardless of how the dialog closes. */
  closeDialog = async () => {
    this.#stopObserving();
    await this.#parentCloseDialog();
  };

  // -----------------------------------------------------------------------
  // Click interceptor: strips color-swatch-navigation BEFORE change fires
  // -----------------------------------------------------------------------

  /**
   * When the user clicks inside a color-swatch fieldset, strip
   * data-color-swatch-navigation from the radio input so the variant picker's
   * change handler doesn't navigate to the PDP.
   *
   * Event order: mousedown → mouseup → click → input → change
   * So by the time `change` fires, the attribute is gone.
   *
   * @param {Event} event
   */
  #handleClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (!target) return;

    // Find the closest color-links fieldset
    const colorFieldset = target.closest('.variant-option--color-links');
    if (!colorFieldset) return;

    // Strip navigation attributes from ALL inputs in this fieldset
    // (not just the clicked one, since clicking a label activates a different input)
    const inputs = colorFieldset.querySelectorAll('input[data-color-swatch-navigation]');
    for (const input of inputs) {
      const targetUrl = input.getAttribute('data-color-target-url') || '';
      input.removeAttribute('data-color-swatch-navigation');
      input.removeAttribute('data-color-target-url');
      if (targetUrl) {
        input.setAttribute('data-connected-product-url', targetUrl);
      }
    }
  };

  // -----------------------------------------------------------------------
  // MutationObserver: watches for morph-reintroduced attributes
  // -----------------------------------------------------------------------

  /**
   * Observes the modal content for DOM changes (from variant picker morphs).
   * When new nodes are added or attributes change, re-patches the content.
   */
  #observeModalContent() {
    this.#stopObserving();

    const modalContent = document.getElementById('cart-edit-modal-content');
    if (!modalContent) return;

    this.#contentObserver = new MutationObserver(() => {
      this.#patchLiveModalContent();
    });

    this.#contentObserver.observe(modalContent, {
      childList: true,
      subtree: true,
      attributeFilter: ['data-color-swatch-navigation', 'on:submit'],
    });
  }

  #stopObserving() {
    if (this.#contentObserver) {
      this.#contentObserver.disconnect();
      this.#contentObserver = null;
    }
  }

  // -----------------------------------------------------------------------
  // Form submit handler
  // -----------------------------------------------------------------------

  /**
   * Handles the form submit by doing a cart edit (remove old + add new).
   *
   * Shopify's /cart/change.js can only change the quantity of an existing
   * line item — it cannot swap the variant. So we:
   *   1. Remove the old line item  (POST /cart/change.js  quantity: 0)
   *   2. Add the new variant       (POST /cart/add.js)
   *   3. Dispatch CartUpdateEvent to refresh the cart UI
   *
   * If step 2 fails we attempt to rollback by re-adding the original variant
   * so the customer doesn't silently lose their cart item.
   *
   * @param {Event} event
   */
  #handleFormSubmit = async (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();

    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;

    const variantInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    if (!variantInput) return;

    const newVariantId = variantInput.value;
    if (!newVariantId || !this.#itemKey) return;

    // Use the original item quantity from the cart, not the form's default
    const quantity = parseInt(this.#itemQuantity, 10) || 1;

    // Disable the submit button while saving
    const submitButton = /** @type {HTMLButtonElement | null} */ (
      form.querySelector('button[type="submit"], button[name="add"]')
    );
    if (submitButton) {
      submitButton.disabled = true;
    }

    // Collect section IDs for section rendering
    const sectionsParam = getSectionsParam();

    try {
      // Step 1: Remove the old line item by setting its quantity to 0.
      // Use the stable line-item key (`id`) instead of the positional `line`
      // index so the correct item is targeted even if the cart changed since
      // the page was rendered (e.g. another tab removed an item).
      const removeBody = JSON.stringify({
        id: this.#itemKey,
        quantity: 0,
      });

      const removeResponse = await fetch(
        `${Theme.routes.cart_change_url}`,
        fetchConfig('json', { body: removeBody })
      );
      const removeText = await removeResponse.text();
      const removeResult = JSON.parse(removeText);

      if (removeResult.status) {
        showButtonError(submitButton, removeResult.message || 'Could not update cart');
        return;
      }

      // Step 2: Add the new variant with the original quantity.
      // If this fails we must attempt to restore the original item.
      let addResult;
      try {
        const addBody = JSON.stringify({
          items: [{ id: parseInt(newVariantId, 10), quantity }],
          sections: sectionsParam,
          sections_url: window.location.pathname,
        });

        const addResponse = await fetch(`${Theme.routes.cart_add_url}`, {
          ...fetchConfig('json', { body: addBody }),
        });
        const addText = await addResponse.text();
        addResult = JSON.parse(addText);
      } catch (addNetworkError) {
        // Network / parse failure – treat as add failure and trigger rollback
        addResult = { status: 'error', message: 'A network error occurred. Please try again.' };
      }

      if (addResult.status) {
        // Add failed – rollback: re-add the original variant.
        // Suppress auto-close so #refreshCartUI (inside rollback) doesn't
        // close the dialog before the user sees the error message.
        const errorMessage = addResult.message || 'Could not save changes';
        this.#suppressAutoClose = true;
        try {
          await this.#rollbackOriginalItem(quantity, sectionsParam);
        } finally {
          this.#suppressAutoClose = false;
        }
        showButtonError(submitButton, errorMessage);
        return;
      }

      // Step 3: Dispatch CartUpdateEvent to refresh the cart UI.
      await this.#refreshCartUI(sectionsParam);
    } catch (error) {
      console.error('Cart edit error:', error);
      // Something unexpected went wrong – refresh the cart UI to stay in sync
      // and surface an error to the user.
      // Suppress auto-close so the error message is visible.
      this.#suppressAutoClose = true;
      try {
        await this.#refreshCartUI(sectionsParam);
      } finally {
        this.#suppressAutoClose = false;
      }
      showButtonError(submitButton, 'Something went wrong. Please try again.');
    }
  };

  /**
   * Attempts to re-add the original variant after a failed cart edit.
   * If the rollback itself fails, we still refresh the cart UI so it
   * reflects the actual server-side state.
   *
   * @param {number} quantity
   * @param {string} sectionsParam
   */
  async #rollbackOriginalItem(quantity, sectionsParam) {
    if (!this.#originalVariantId) {
      // Can't rollback without the original variant ID – just refresh
      await this.#refreshCartUI(sectionsParam);
      return;
    }

    try {
      const rollbackBody = JSON.stringify({
        items: [{ id: parseInt(this.#originalVariantId, 10), quantity }],
      });

      const rollbackResponse = await fetch(`${Theme.routes.cart_add_url}`, {
        ...fetchConfig('json', { body: rollbackBody }),
      });
      const rollbackText = await rollbackResponse.text();
      const rollbackResult = JSON.parse(rollbackText);

      if (rollbackResult.status) {
        console.error('Cart rollback also failed:', rollbackResult.message);
      }
    } catch (rollbackError) {
      console.error('Cart rollback network error:', rollbackError);
    }

    // Always refresh the cart UI so it matches server state
    await this.#refreshCartUI(sectionsParam);
  }

  /**
   * Fetches the latest cart state and dispatches a CartUpdateEvent
   * to keep the cart UI in sync with the server.
   *
   * @param {string} sectionsParam
   */
  async #refreshCartUI(sectionsParam) {
    try {
      const cartResponse = await fetch(
        `${Theme.routes.cart_url}.js?sections=${sectionsParam}&sections_url=${encodeURIComponent(window.location.pathname)}`
      );
      const cartData = await cartResponse.json();

      this.dispatchEvent(
        new CartUpdateEvent(cartData, this.id, {
          itemCount: cartData.item_count,
          source: 'cart-edit-modal',
          sections: cartData.sections,
        })
      );
    } catch (refreshError) {
      console.error('Failed to refresh cart UI:', refreshError);
    }
  }

  // -----------------------------------------------------------------------
  // Variant update handler
  // -----------------------------------------------------------------------

  /**
   * After a variant update (e.g. combined listing product change),
   * immediately re-patch the live modal content.
   */
  #handleVariantUpdate = () => {
    this.#patchLiveModalContent();
  };

  /**
   * Patches the live modal content.
   * - Strips color-swatch-navigation attributes
   * - Removes on:submit from product-form-component
   * - Removes data-template-product-match from variant-picker
   * - Ensures button says "Update"
   */
  #patchLiveModalContent() {
    const modalContent = document.getElementById('cart-edit-modal-content');
    if (!modalContent) return;

    // Temporarily disconnect observer to avoid re-triggering while we patch
    this.#stopObserving();

    // Remove on:submit from product-form-component
    const productFormComponent = modalContent.querySelector('product-form-component');
    if (productFormComponent?.hasAttribute('on:submit')) {
      productFormComponent.removeAttribute('on:submit');
    }

    // Fix color swatch navigation
    stripColorSwatchNavigation(modalContent);

    // Prevent variant picker from acting as PDP
    const variantPicker = modalContent.querySelector('variant-picker');
    if (variantPicker?.hasAttribute('data-template-product-match')) {
      variantPicker.removeAttribute('data-template-product-match');
    }

    // Ensure button says "Update"
    replaceAddToCartText(modalContent);

    // Sync radio checked state after morph
    syncRadioCheckedState(modalContent);

    // Re-start observing
    this.#observeModalContent();
  }

  /**
   * Auto-close the dialog on successful cart update.
   * Skipped when #suppressAutoClose is set (error-recovery paths that
   * refresh the cart UI but still need the dialog open for the error message).
   * @param {CartUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    if (this.#suppressAutoClose) return;
    this.closeDialog();
  };
}

if (!customElements.get('cart-edit-dialog')) {
  customElements.define('cart-edit-dialog', CartEditDialog);
}
